<script setup lang="ts">
import type { Form, FormErrorEvent, FormSubmitEvent, ToastProps } from '#ui/types';
import { adaptFormDataToDto, phoneValidator } from '@lib/shared';
import { useMutation } from '@tanstack/vue-query';
import lodashSnakeCase from 'lodash-es/snakeCase';
import { z } from 'zod';

import { STORYBLOK_ROUTES } from '#shared/constants/storyblok';
import {
    apiUserSignUp,
    BackendErrorCode,
    type BackendErrorEntity,
    type BodyType,
    type ErrorType,
    type UserEntity,
    type UserSignupBodyDto,
} from '~/apiClient';

interface SignupFormProps {
    email: string;
    phone?: string;
}

// PROPS
const { email, phone } = defineProps<SignupFormProps>();

// EVENTS
const emit = defineEmits<{
    success: [UserEntity];
    editEmail: [];
}>();

// COMPOSABLES
const { locale, tt } = useTypedI18n();
const { notifyBackendError, notifyError, notifyGenericError } = useNotification();
const { getFormErrors } = useBackendErrors();
const { logError, logInfo } = useLogs();
const { trackSignupFieldClick, trackSignupFormError, trackSignupCreateClick } = useTracking();
const { getToken, formatRequestOptions } = useInteractionOnlyTurnstile('signup');
const { openRequestPasswordResetModal } = useModals();
const { trackSignupCheckEmailView } = useTracking();
const { mutateAsync: mutateSignup, isPending: isSubmitting } = useMutation<
    Awaited<ReturnType<typeof apiUserSignUp>>,
    ErrorType<BackendErrorEntity>,
    { data: BodyType<UserSignupBodyDto>; token: string | null },
    unknown
>({
    mutationFn: ({ data, token }) => apiUserSignUp(data, formatRequestOptions(token)),
});
const { trackSignupStandardFormSubmitted } = useTracking();

// DATA
const formSchema = z.object({
    phone: zodRequiredString(tt('validation.msg.required', { field: tt('signup.fields.phone') })).refine(value => !value || phoneValidator(value), {
        message: tt('validation.msg.phone'),
    }),
    email: z.email(tt('validation.msg.invalid', { field: tt('login.email') })),
    password: zodRequiredString(tt('validation.msg.required', { field: tt('signup.fields.password') })).refine(isSecurePassword, {
        message: tt('validation.msg.passwordWeak'),
    }),
});
type FormSchema = typeof formSchema;
type FormData = z.infer<FormSchema>;
const formData = reactive<Partial<FormData>>({
    phone: phone ?? undefined,
    email,
    password: '',
});
const form = useTemplateRef<Form<FormSchema>>('form');

const onSubmit = async (event: FormSubmitEvent<FormData>): Promise<void> => {
    let turnstileToken: string | null = null;

    try {
        turnstileToken = await getToken();
    } catch {
        notifyError({
            title: tt('validation.msg.captcha'),
            description: tt('notifications.tryAgain'),
        });

        return;
    }

    const { data } = event;

    const body: UserSignupBodyDto = adaptFormDataToDto({
        phone: data.phone,
        email: data.email,
        password: data.password,
        language: localeToLanguage(locale.value),
    });

    mutateSignup({ data: body, token: turnstileToken })
        .then(user => {
            trackSignupStandardFormSubmitted(user);
            emit('success', user);
            trackSignupCheckEmailView(formData.email);
        })
        .catch(async (apiError: ErrorType<BackendErrorEntity>) => {
            logError('Error during signup step create', {
                apiError: apiError.data,
                body,
            });

            if (!apiError.data?.error) {
                notifyGenericError();
                trackSignupFormError('null');

                return;
            }

            const { error } = apiError.data;

            const actions: ToastProps['actions'] =
                error.code === BackendErrorCode.SignupUserAlreadyExists
                    ? [{ label: tt('login.forgotPassword'), onClick: () => openRequestPasswordResetModal(), variant: 'link' }]
                    : undefined;
            notifyBackendError({
                error,
                actions,
            });
            trackSignupFormError(lodashSnakeCase(error.code));

            const formErrors = getFormErrors<UserSignupBodyDto, FormData>(apiError, {
                email: 'email',
                password: 'password',
            });
            form.value?.setErrors(formErrors);
            await nextTick();
        });
};

const onError = (errorEvent: FormErrorEvent): void => {
    logInfo('Error before signup step create', {
        formErrors: errorEvent.errors,
        formData,
    });

    errorEvent.errors.forEach(e => {
        trackSignupFormError(lodashSnakeCase(`${e.name}_invalid`));
    });

    if (!errorEvent.errors.length) return;

    scrollToFormError();
};
</script>

<template>
    <UForm ref="form" class="flex flex-col items-center gap-4" :state="formData" :schema="formSchema" @submit="onSubmit" @error="onError">
        <p class="text-muted text-center font-medium">{{ tt('signup.subtitle') }}</p>

        <div class="flex w-full flex-col gap-4">
            <UFormField :label="tt('login.email')" name="email">
                <UInput v-model.trim="formData.email" disabled>
                    <template #trailing>
                        <UButton :label="tt('signup.fields.email.change')" size="sm" variant="ghost" square @click="emit('editEmail')" />
                    </template>
                </UInput>
            </UFormField>

            <UFormField :label="tt('signup.fields.phone')" name="phone" required>
                <template #hint>
                    <BasePhoneHint />
                </template>
                <BasePhoneInput v-model.trim="formData.phone" :disabled="isSubmitting" />
            </UFormField>

            <UFormField :label="tt('signup.fields.password')" name="password" required>
                <template #hint>
                    <BasePasswordHint />
                </template>
                <template #default>
                    <BasePasswordInput
                        v-model="formData.password"
                        autocomplete="new-password"
                        :disabled="isSubmitting"
                        @click="trackSignupFieldClick('password')"
                    />
                </template>
            </UFormField>
        </div>

        <div class="w-full">
            <p class="flex flex-col gap-1">
                <BaseI18nT tag="span" keypath="signup.fields.termsConditions">
                    <NuxtLinkLocale class="text-primary-500 font-bold" :to="STORYBLOK_ROUTES.TERMS_AND_CONDITIONS" target="_blank">
                        {{ tt('termsConditions.link') }}
                    </NuxtLinkLocale>
                </BaseI18nT>
                <BaseI18nT tag="span" keypath="signup.fields.privacy">
                    <NuxtLinkLocale class="text-primary-500 font-bold" :to="STORYBLOK_ROUTES.PRIVACY_POLICY" target="_blank">
                        {{ tt('privacy.link') }}
                    </NuxtLinkLocale>
                </BaseI18nT>
            </p>
        </div>

        <TurnistileChallenge />

        <UButton :label="tt('signup.createAccount')" type="submit" size="xl" block :loading="isSubmitting" @click="trackSignupCreateClick" />
    </UForm>
</template>
